// Utility layer (TS-2.5.04) - pure, no side effects. The roadmap detail cards' owner join
// (decisions.md D-280): a card renders from `ProjectView` (the RoadmapView composition's own DTO),
// which carries neither `productKey` nor `businessOwner` - those live on `ProjectSummary`
// (decisions.md D-279/D-270). This module joins a card's projectKey -> ProjectSummary ->
// ProductRecord, exactly the way the Team/Product toggle's own regroupBoardByProduct.ts already
// joins a chip's projectKey, so both consumers read the SAME projects+products fetch
// (hooks/useProjectProductJoin.ts) rather than each inventing its own resolution.
import type { ProductRecord, ProjectSummary } from "../../../api/generated/contracts";

export interface ProjectOwnerInfo {
  /** The assigned product's name, or null when the project carries no product (or the join has
   *  not resolved yet). */
  readonly productName: string | null;
  /** D-279's effective business owner: the project's own value if it names one, else the
   *  assigned product's, else null when neither does. */
  readonly businessOwner: string | null;
}

const EMPTY_INFO: ProjectOwnerInfo = { productName: null, businessOwner: null };

/** Builds one lookup, keyed by project key, so every card can read its own owner info in O(1)
 *  rather than re-scanning `projects`/`products` per card render. */
export function resolveProjectOwners(
  projects: readonly ProjectSummary[],
  products: readonly ProductRecord[],
): ReadonlyMap<string, ProjectOwnerInfo> {
  const productsByKey = new Map(products.filter((product) => product.key).map((product) => [product.key as string, product]));

  const result = new Map<string, ProjectOwnerInfo>();
  for (const project of projects) {
    const product = project.productKey ? (productsByKey.get(project.productKey) ?? null) : null;
    result.set(project.projectKey, {
      productName: product?.name ?? null,
      businessOwner: project.businessOwner ?? product?.businessOwner ?? null,
    });
  }
  return result;
}

/** The lookup's own safe accessor: a project key the join has not (yet) seen - the fetch is still
 *  loading, or failed, or (should never happen) the roadmap board named a project the projects
 *  list did not return - reads as "no product, no resolved owner" rather than throwing. */
export function ownerInfoFor(map: ReadonlyMap<string, ProjectOwnerInfo>, projectKey: string): ProjectOwnerInfo {
  return map.get(projectKey) ?? EMPTY_INFO;
}
